<template>
    <modal size="xl" @close="$emit('close')" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <span v-if="deductionType">Update Deduction Type: </span><span v-if="deductionType" class="text-thin text-nowrap">{{ deductionType.name }}</span>
            <span v-if="!deductionType">Add New Deduction Type</span>
        </template>
        <slot>
            <form class="mui-form" @submit.prevent="onSubmit()">
                <div class="form-row">
                    <div class="col">
                        <form-input v-model="formData.name" :errors="errors.name" type="text" label="Name" />
                    </div>
                </div>

                <div class="form-row">
                    <div class="col">
                        <form-input v-model="formData.category" :errors="errors.category" type="select" label="Category" :options="categoryOptions" />
                    </div>
                </div>

                <div class="form-row" v-if="hasDesignation">
                    <div class="col">
                        <form-input
                            v-model="formData.designation"
                            :errors="errors.designation"
                            type="select"
                            label="Designation"
                            :options="designationOptions"
                            :helper-text="designationHelperText"
                        />
                    </div>
                </div>

                <div class="form-row mb-2">
                    <div class="col">
                        <form-input v-model="formData.for_primary_benefit_of_employer" :errors="errors.for_primary_benefit_of_employer" type="checkbox" label="For Primary Benefit of Employer" />
                    </div>
                </div>


                <div class="form-row mt-4">
                    <div class="col">
                        <form-input v-model="formData.symmetry_benefit_type" :errors="errors.symmetry_benefit_type" type="select" label="Symmetry Benefit Type" :options="symmetryBenefitTypeOptions" />
                    </div>
                </div>

                <div class="form-row">
                    <div class="col-6">
                        <form-input v-model="formData.w2_box" :errors="errors.w2_box" type="select" label="W2 Box" :options="w2BoxOptions" />
                    </div>
                    <div class="col-6">
                        <form-input v-model="formData.w2_code" :errors="errors.w2_code" type="text" label="W2 Code" />
                    </div>
                </div>

                <div class="form-row">
                    <div class="col-6">
                        <form-input
                            v-model="formData.annual_contribution_limit"
                            :errors="errors.annual_contribution_limit"
                            type="text"
                            label="Annual Contribution Limit"
                            helper-text="Annual limit in dollars. Leave blank for no limit; a value of 0 means nothing may be contributed."
                        />
                    </div>

                    <div class="col-6" v-if="isCatchup">
                        <form-input
                            v-model="formData.annual_super_catchup_contribution_limit"
                            :errors="errors.annual_super_catchup_contribution_limit"
                            type="text"
                            label="Annual Super Catch-Up Contribution Limit"
                            helper-text="Applies to participants who reach age 60&ndash;63 at any point in the calendar year. Leave blank to give them the ordinary limit above."
                        />
                    </div>
                </div>

                <p class="mt-4 mb-0">
                    <a href class="text-muted small" @click.prevent="extraOpen = !extraOpen">Danger: Developer Only</a>
                </p>

                <div class="mt-2 mb-2" v-show="extraOpen">
                    <div class="form-row mb-2">
                        <div class="col">
                            <form-input
                                v-model="formData.allow_multiple_per_company"
                                :errors="errors.allow_multiple_per_company"
                                type="checkbox"
                                label="Allow Multiple Per Company"
                                helper-text="When enabled, a company may have more than one deduction of this type."
                            />
                        </div>
                    </div>

                    <div class="form-row mt-2 mb-2">
                        <div class="col">
                            <label class="form-label">Fields to Customize</label>
                            <div v-for="option in fieldsToCustomizeOptions" :key="option.value" class="form-check">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    :value="option.value"
                                    :id="`ftc_${option.value}`"
                                    v-model="formData.fields_to_customize"
                                />
                                <label class="form-check-label" :for="`ftc_${option.value}`">{{ option.label }}</label>
                            </div>
                            <div v-if="errors.fields_to_customize" class="invalid-feedback d-block">{{ errors.fields_to_customize }}</div>
                        </div>
                    </div>
                </div>
            </form>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row>
                <button type="button" class="btn btn-block btn-outline-primary" @click.prevent="close()">Cancel</button>
                <button type="button" class="btn btn-block btn-primary" @click.prevent="onSubmit()">Save</button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'

export default {
    props: ['deductionType', ],
    mixins: [FormMixin, ModalMixin, ],
    computed: {
        categoryOptions() {
            return [
                {text: 'Deferred Compensation', value: 'deferred-comp'},
                {text: 'Insurance', value: 'insurance'},
                {text: 'Other Benefits', value: 'other-benefits'},
                {text: 'Misc', value: 'misc'},
                {text: 'Retirement Plan Loan', value: 'retirement-loan'},
            ]
        },
        isDeferredComp() {
            return this.formData.category == 'deferred-comp'
        },
        isInsurance() {
            return this.formData.category == 'insurance'
        },
        isRetirementLoan() {
            return this.formData.category == 'retirement-loan'
        },
        // Only these three categories carry designations, and each one has its own set:
        // the model rejects a designation that belongs to another category.
        hasDesignation() {
            return this.isDeferredComp || this.isInsurance || this.isRetirementLoan
        },
        designationHelperText() {
            if (this.isInsurance) {
                return 'Insurance plans pick their deduction types by designation, from the plan type and whether the premium is pre-tax. At most one type may carry each one. Set the Symmetry benefit type to match: Benefit 125 for a Section 125 designation, Benefit Custom for a post-tax one.'
            }

            if (this.isRetirementLoan) {
                return 'Deferred compensation plans pick the deduction type their loan repayments are withheld under by designation, from the plan type. At most one type may carry each one. A repayment is after-tax money and is not a contribution, so set Benefit Custom as the Symmetry benefit type and leave the W-2 box and contribution limit empty.'
            }

            return 'Deferred compensation plans pick their deduction types by designation. At most one type may carry each one.'
        },
        // Only a catch-up deduction type can carry a super catch-up limit; the model
        // rejects one anywhere else.
        isCatchup() {
            return [
                '401k-catchup',
                '401k-roth-catchup',
                '403b-catchup',
                '403b-roth-catchup',
                'simple-ira-catchup',
            ].includes(this.formData.designation)
        },
        designationOptions() {
            if (this.isRetirementLoan) {
                // NB: no SIMPLE IRA. A loan from an IRA is a prohibited transaction, so
                // DeferredCompPlan.PLAN_TYPE_DEDUCTION_DESIGNATIONS gives that plan type
                // no loan designation to look up.
                return [
                    {text: '-', value: ''},
                    {text: '401(k) Loan', value: '401k-loan'},
                    {text: 'Roth 401(k) Loan', value: '401k-roth-loan'},
                    {text: '403(b) Loan', value: '403b-loan'},
                    {text: 'Roth 403(b) Loan', value: '403b-roth-loan'},
                ]
            }

            if (this.isInsurance) {
                return [
                    {text: '-', value: ''},
                    {text: 'Health Insurance (Section 125)', value: 'health-sec-125'},
                    {text: 'Dental Insurance (Section 125)', value: 'dental-sec-125'},
                    {text: 'Vision Insurance (Section 125)', value: 'vision-sec-125'},
                    {text: 'Health Insurance (Post-Tax)', value: 'health-posttax'},
                    {text: 'Dental Insurance (Post-Tax)', value: 'dental-posttax'},
                    {text: 'Vision Insurance (Post-Tax)', value: 'vision-posttax'},
                ]
            }

            return [
                {text: '-', value: ''},
                {text: '401(k)', value: '401k'},
                {text: '401(k) Catch-Up', value: '401k-catchup'},
                {text: 'Roth 401(k)', value: '401k-roth'},
                {text: 'Roth 401(k) Catch-Up', value: '401k-roth-catchup'},
                {text: '403(b)', value: '403b'},
                {text: '403(b) Catch-Up', value: '403b-catchup'},
                {text: 'Roth 403(b)', value: '403b-roth'},
                {text: 'Roth 403(b) Catch-Up', value: '403b-roth-catchup'},
                {text: '408(p) SIMPLE IRA', value: 'simple-ira'},
                {text: '408(p) SIMPLE IRA Catch-Up', value: 'simple-ira-catchup'},
            ]
        },
        symmetryBenefitTypeOptions() {
            return [
                {text: '-', value: ''},
                {text: 'Benefit 125', value: 'Benefit125'},
                {text: 'Benefit 401K', value: 'Benefit401K'},
                {text: 'Benefit 403B', value: 'Benefit403B'},
                {text: 'Benefit 457', value: 'Benefit457'},
                {text: 'Benefit Roth 401K', value: 'BenefitRoth401K'},
                {text: 'Benefit Roth 403B', value: 'BenefitRoth403B'},
                {text: 'Benefit Roth 457', value: 'BenefitRoth457'},
                {text: 'Benefit FSA', value: 'BenefitFSA'},
                {text: 'Benefit FSA Dependent Care', value: 'BenefitFSADependentCare'},
                {text: 'Benefit HSA', value: 'BenefitHSA'},
                {text: 'Benefit Simple IRA', value: 'BenefitSimpleIRA'},
                {text: 'Benefit Simple IRA Nonelective', value: 'BenefitSimpleIRANonelective'},
                {text: 'Benefit Custom', value: 'BenefitCustom'},
            ]
        },
        w2BoxOptions() {
            return [
                {text: '-', value: null},
                {text: 'Box 12', value: 12},
                {text: 'Box 14', value: 14},
            ]
        },
        fieldsToCustomizeOptions() {
            return [
                {value: 'name', label: 'Name'},
                {value: 'for_primary_benefit_of_employer', label: 'For Primary Benefit of Employer'},
            ]
        },
    },
    data() {
        return {
            formData: this.makeFormData(),
            extraOpen: false,
        }
    },
    watch: {
        // Each category has its own designations, so one carried over from the category
        // just left would be rejected by the model.
        'formData.category'() {
            if (!this.hasDesignation || !this.designationOptions.find(o => o.value == this.formData.designation)) {
                this.formData.designation = ''
            }
        },
        'formData.designation'() {
            if (!this.isCatchup) {
                this.formData.annual_super_catchup_contribution_limit = ''
            }
        },
    },
    methods: {
        makeFormData() {
            if (this.deductionType) {
                return {
                    name: this.deductionType.name,
                    category: this.deductionType.category,
                    designation: this.deductionType.designation || '',
                    for_primary_benefit_of_employer: this.deductionType.for_primary_benefit_of_employer,
                    symmetry_benefit_type: this.deductionType.symmetry_benefit_type,
                    w2_box: this.deductionType.w2_box,
                    w2_code: this.deductionType.w2_code || '',
                    annual_contribution_limit: this.deductionType.annual_contribution_limit ?? '',
                    annual_super_catchup_contribution_limit: this.deductionType.annual_super_catchup_contribution_limit ?? '',
                    allow_multiple_per_company: this.deductionType.allow_multiple_per_company,
                    fields_to_customize: this.deductionType.fields_to_customize || [],
                }
            } else {
                return {
                    name: '',
                    category: 'misc',
                    designation: '',
                    for_primary_benefit_of_employer: false,
                    symmetry_benefit_type: '',
                    w2_box: null,
                    w2_code: '',
                    annual_contribution_limit: '',
                    annual_super_catchup_contribution_limit: '',
                    allow_multiple_per_company: false,
                    fields_to_customize: [],
                }
            }
        },
        getFormData() {
            return this.formData
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            let promise
            if (this.deductionType) {
                promise = this.$api.put(`/admin/tax-engine/deduction-types/${this.deductionType.id}`, this.getFormData())
            }
            else {
                promise = this.$api.post(`/admin/tax-engine/deduction-types`, this.getFormData())
            }

            this.$store.dispatch('START_LOADING')
            return promise.then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated', resp)
                this.close()
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
            })
        },
    }
}
</script>
